import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { GtmService } from '@app-shared/services/analytics/gtm.service';
import { IntercomService } from '@app-shared/services/intercom/intercom.service';
import { LoggerService } from '@app-shared/services/logger/logger.service';
import { OnboardingService } from '@app-shared/services/onboarding/onboarding.service';
import { PartnerFundService } from './partner-fund.service';
import { FLOW_BASE_PATH, resolveLegacyPath, resolveRedirectedPath } from '../legacy-paths';

const FLOW = FLOW_BASE_PATH;

/** Entry page. Not a screen of its own, so the progress indicator skips it. */
const ENTRY_PATH = '/kiwisaver/onboarding';

/** Where a completed partner switch lands. Leaves onboarding, so not counted as a screen. */
const DASHBOARD_PATH = '/dashboard';

/** Quiz steps before the preference screens. */
const QUIZ_STEPS = [
  `${FLOW}/goal`,
  `${FLOW}/goal-timeline`,
  `${FLOW}/personality/investing`,
  `${FLOW}/personality/concern`,
  `${FLOW}/personality/value`,
  `${FLOW}/personality/summary`,
];

/** V3 ethics flow: 8-slider ethics chooser + sustainability toggle. */
const V3_PREFERENCE_STEPS = [`${FLOW}/ethics-chooser`, `${FLOW}/sustainability`];

/** V4 preference flow: split per-question screens + priority picker. */
const V4_PREFERENCE_STEPS = [
  `${FLOW}/avoidance`,
  `${FLOW}/pref-sustainability`,
  `${FLOW}/priority-picker`,
  // Rewards aren't ready yet — temporarily removed from the flow so users skip it.
  // The route + component remain in place; re-add this step to bring it back.
  // `${FLOW}/rewards-toggle`,
];

/** Review, reveal and the advice page, shared by both branches. */
const ADVICE_STEPS = [`${FLOW}/profile`, `${FLOW}/reveal`, `${FLOW}/advice`];

/** Partner funds: BetterSaver runs the switch, so we collect details and transfer. */
const PARTNER_STEPS = [
  `${FLOW}/contact-details`,
  `${FLOW}/identification`,
  `${FLOW}/tax-details`,
  `${FLOW}/check-and-confirm`,
  `${FLOW}/transfer`,
  DASHBOARD_PATH,
];

/** Non-partner funds: no personal details collected, the user switches with the provider. */
const NON_PARTNER_STEPS = [`${FLOW}/switch-instructions`];

export type FlowBranch = 'partner' | 'non-partner';

@Injectable()
export class FlowNavigatorService {
  private _baseRouter = inject(Router);
  private logger = inject(LoggerService);
  private onboardingService = inject(OnboardingService);
  private intercomService = inject(IntercomService);
  private gtmService = inject(GtmService);
  private partnerFundService = inject(PartnerFundService);

  private _currentPosition = 0;
  private _previousKnownPath = '';
  private _flowPath: string[] = [];

  constructor() {
    // Boot Intercom as an anonymous lead the moment a user enters onboarding,
    // before they create an account, so behavioural/funnel attributes can attach
    // to the lead pre-signup and drop-offs remain nurturable. This service is
    // route-scoped, so this runs once per onboarding session. boot() is
    // attribute-free (no PII at boot) and idempotent, so the post-auth boot does
    // not double-inject; on account creation the lead is merged via identify().
    this.intercomService.boot();
  }

  /**
   * The ordered flow for this user: quiz, preferences (V4, or V3 for mid-flow
   * users who started before V4), review, reveal, advice, then the partner or
   * non-partner branch.
   */
  private get mainFlowPath(): string[] {
    if (this._flowPath.length === 0) {
      this._flowPath = this.buildFlowPath();
    }
    return this._flowPath;
  }

  /**
   * @param url When given and it belongs to one branch, that branch is used,
   *   so a user already inside a branch keeps their place on refresh.
   */
  private buildFlowPath(url?: string): string[] {
    // Default to V4. Only fall back to V3 if user has existing V3 ethics data
    // (mid-onboarding user who started before V4 was deployed).
    const hasV3Data =
      this.onboardingService.onboardingData.ethics_adult_entertainment !== undefined;
    const hasV4Data = this.onboardingService.onboardingData.selected_preferences !== undefined;
    const useV3 = hasV3Data && !hasV4Data;
    const preferenceSteps = useV3 ? V3_PREFERENCE_STEPS : V4_PREFERENCE_STEPS;
    const branchSteps = this.resolveBranch(url) === 'partner' ? PARTNER_STEPS : NON_PARTNER_STEPS;
    return [ENTRY_PATH, ...QUIZ_STEPS, ...preferenceSteps, ...ADVICE_STEPS, ...branchSteps];
  }

  private resolveBranch(url?: string): FlowBranch {
    if (url !== undefined && PARTNER_STEPS.includes(url)) {
      return 'partner';
    }
    if (url !== undefined && NON_PARTNER_STEPS.includes(url)) {
      return 'non-partner';
    }
    return this.partnerFundService.isRecommendedFundPartner() ? 'partner' : 'non-partner';
  }

  /** Force rebuild of flow path (call after V4 data is first written). */
  refreshFlowPath(): void {
    this._flowPath = this.buildFlowPath();
  }

  get currentPath(): string {
    return this.mainFlowPath[this._currentPosition];
  }

  /** Which branch the flow takes after the advice page. */
  get branch(): FlowBranch {
    return this.mainFlowPath.includes(PARTNER_STEPS[0]) ? 'partner' : 'non-partner';
  }

  /**
   * Screen number for the progress indicator, 1-based. The entry page and the
   * dashboard aren't screens, so the first quiz screen is 1 and the last screen
   * of either branch equals `totalSteps`.
   */
  get currentStep(): number {
    return Math.max(this.progressScreens.indexOf(this.currentPath) + 1, 0);
  }

  /** Number of screens on this user's branch, for the progress indicator. */
  get totalSteps(): number {
    return this.progressScreens.length;
  }

  private get progressScreens(): string[] {
    return this.mainFlowPath.filter((path) => path !== ENTRY_PATH && path !== DASHBOARD_PATH);
  }

  async navigateNext(): Promise<void> {
    const fromPath = this.currentPath;
    this._previousKnownPath = this.currentPath;

    // The partner flag is only known once the advice page has a fund, so the
    // branch is settled when leaving the advice page rather than at flow start.
    if (fromPath === ADVICE_STEPS[ADVICE_STEPS.length - 1]) {
      this._flowPath = this.buildFlowPath();
    }

    this._currentPosition++;
    const nextUrl = this.mainFlowPath[this._currentPosition];

    this.updateLoggerContext();
    this.logger.nav('navigate_next', { from: fromPath, to: nextUrl, direction: 'forward' });
    this.emitStepEvent('forward');

    // Fire-and-forget lead-nurture push: snapshot the latest onboarding state to
    // the anonymous Intercom lead on each advance. NOT awaited — must never
    // delay navigation.
    this.pushIntercomAttributes();

    await this._baseRouter.navigate([nextUrl]);
  }

  async navigatePrevious(): Promise<void> {
    const fromPath = this.currentPath;
    this._previousKnownPath = this.currentPath;
    this._currentPosition--;
    const prevUrl = this.mainFlowPath[this._currentPosition];

    this.updateLoggerContext();
    this.logger.nav('navigate_previous', { from: fromPath, to: prevUrl, direction: 'back' });
    this.emitStepEvent('back');

    this.pushIntercomAttributes();

    await this._baseRouter.navigate([prevUrl]);
  }

  async jumpPrevious(jumpToPath: number): Promise<void> {
    const fromPath = this.currentPath;
    this._previousKnownPath = this.currentPath;
    this._currentPosition = jumpToPath;
    const toUrl = this.mainFlowPath[jumpToPath];

    this.updateLoggerContext();
    this.logger.nav('navigate_jump', { from: fromPath, to: toUrl, jumpTo: jumpToPath });
    this.emitStepEvent('back');

    this.pushIntercomAttributes();

    await this._baseRouter.navigate([toUrl]);
  }

  /**
   * Jump back to a screen by its path, for edit links. Paths stay correct when
   * screens are added or removed; step numbers don't. A preference screen that
   * isn't in this user's flow version (V3 vs V4) lands on the first preference
   * screen instead.
   */
  async jumpToPath(path: string): Promise<void> {
    let index = this.mainFlowPath.indexOf(path);
    if (index === -1 && [...V3_PREFERENCE_STEPS, ...V4_PREFERENCE_STEPS].includes(path)) {
      index = 1 + QUIZ_STEPS.length;
    }
    if (index === -1) {
      this.logger.nav('navigate_jump_unknown_path', { from: this.currentPath, to: path });
      return;
    }
    await this.jumpPrevious(index);
  }

  setPositionFromRouteURL(url: string): void {
    url = url.split('?')[0];

    // Mid-flow users who reload on a removed path get redirected to its replacement.
    // Resume-only paths still have screens, so they are left where they are.
    const redirectTo = resolveRedirectedPath(url);
    if (redirectTo !== url) {
      this._flowPath = this.buildFlowPath();
      this._currentPosition = this.mainFlowPath.indexOf(redirectTo);
      this.updateLoggerContext();
      this._baseRouter.navigate([redirectTo]);
      return;
    }

    // Rebuild flow path in case onboarding data changed
    this._flowPath = this.buildFlowPath(url);
    this._currentPosition = this.mainFlowPath.indexOf(url);

    // If URL not found in current flow (V3 user hitting V4 route or vice versa),
    // try the other flow's routes to find position
    if (this._currentPosition === -1) {
      const altSteps =
        this.onboardingService.onboardingData.selected_preferences !== undefined
          ? V3_PREFERENCE_STEPS
          : V4_PREFERENCE_STEPS;
      const altIdx = altSteps.indexOf(url);
      if (altIdx !== -1) {
        // User is on the wrong flow version — redirect to equivalent position
        const preferenceStartIdx = 1 + QUIZ_STEPS.length;
        this._currentPosition = preferenceStartIdx;
      }
    }

    this.updateLoggerContext();
  }

  /**
   * The replacement for a removed flow path (see `legacy-paths.ts`), or the url
   * unchanged when it is not mapped. Called by the onboarding resolver to resume
   * a user whose saved page no longer exists.
   */
  resolveLegacyPath(url: string): string {
    return resolveLegacyPath(url);
  }

  getNextPathFrom(url: string): string | null {
    url = url.split('?')[0];
    const path = this.buildFlowPath(url);
    const index = path.indexOf(url);
    if (index === -1 || index >= path.length - 1) {
      return null;
    }
    return path[index + 1];
  }

  private updateLoggerContext(): void {
    this.logger.setFlowContext(this._currentPosition, this.mainFlowPath.length);
  }

  /**
   * Emit an ordered `onboarding_step` GTM event on each in-flow navigation.
   *
   * The router `page_view` only carries `page_path`, which can't be ordered
   * into a funnel (and inflates late screens because refreshes / resume
   * deep-links all fire it). This event is fired ONLY from the deliberate
   * next/back/jump navigation methods — never on refresh or resolver-driven
   * resume — so a funnel keyed on the numeric `flow_step` reflects genuine
   * in-session progression. `flow_direction` lets a report isolate forward
   * movement (filter `= 'forward'`) from back-tracking.
   *
   * `flow_step` (the index) is the reliable ordering key; `flow_name` is a
   * stable, collision-free label (the path after `/flow/`, so
   * `personality/investing` stays distinct from any other `investing`).
   * Steps after the advice page differ by branch, so `flow_branch` lets a
   * report split the partner funnel from the non-partner one.
   *
   * Safe in every environment — GtmService no-ops when gtm.js is not loaded.
   */
  private emitStepEvent(direction: 'forward' | 'back'): void {
    const path = this.currentPath;
    if (!path) {
      return;
    }
    this.gtmService.pushTag({
      event: 'onboarding_step',
      flow_name: this.flowNameFromPath(path),
      flow_step: this._currentPosition,
      flow_total_steps: this.mainFlowPath.length,
      flow_direction: direction,
      flow_branch: this.branch,
    });
  }

  /**
   * Derive a stable step name from a flow URL: the segment(s) after `/flow/`
   * (e.g. `personality/investing`), falling back to the last path segment for
   * non-flow entries like `/kiwisaver/onboarding` or `/dashboard`.
   */
  private flowNameFromPath(path: string): string {
    const marker = '/flow/';
    const markerIdx = path.indexOf(marker);
    if (markerIdx !== -1) {
      return path.slice(markerIdx + marker.length);
    }
    const segments = path.split('/').filter(Boolean);
    return segments[segments.length - 1] ?? path;
  }

  /**
   * Push the latest onboarding snapshot to the user's Intercom contact via the
   * backend. Fired on each navigation so attributes accumulate incrementally and
   * pre-signup drop-offs remain nurturable. Works for anonymous and identified
   * users alike: the anonymous onboarding user is a Firebase anonymous-auth user
   * whose uid is the `person_id`, so the callable re-derives the attributes from
   * Firestore and writes them server-side (the browser Messenger 403s attribute
   * writes once the user is identified — see investigation BS-I-478).
   *
   * Fire-and-forget: callers must not await this, and it must never throw into
   * the nav path (sync routes its own failures through Sentry).
   */
  private pushIntercomAttributes(): void {
    try {
      void this.intercomService.sync(this.onboardingService.personId).catch(() => {});
    } catch {
      // Never let lead-nurture telemetry break navigation.
    }
  }

  /**
   * Trigger the server-side Intercom sync the moment the recommendation screen
   * resolves a fund — NOT on the later "Start the switch" click. The per-advance
   * push only fires on navigation, so leads who view the recommendation but
   * abandon would otherwise carry none of it; this lands the recommendation funds,
   * `OB Recommendation Shown`, the selected fund id, and the human-readable
   * `OB Recommended Fund Name` (the backend derives the name from the persisted
   * fund id) for that abandon segment.
   *
   * Fire-and-forget: callers must not await this, and it never throws into the
   * caller's path (sync's failures are Sentry-wrapped).
   */
  pushRecommendationToLead(): void {
    try {
      void this.intercomService.sync(this.onboardingService.personId).catch(() => {});
    } catch {
      // Never let lead-nurture telemetry break the recommendation screen.
    }
  }

  /**
   * Trigger the server-side Intercom sync when a contact field (email, name) is
   * captured/validated, so a user who enters their email then abandons is still a
   * nurturable lead. The backend re-derives the contact slice from the persisted
   * onboarding doc, so the caller must persist the captured value first.
   *
   * Fire-and-forget: callers must not await this, and it never throws into the
   * caller's path (sync's failures are Sentry-wrapped).
   */
  pushContactToLead(): void {
    try {
      void this.intercomService.sync(this.onboardingService.personId).catch(() => {});
    } catch {
      // Never let lead-nurture telemetry break the capture/validation path.
    }
  }

  sustainabilityMagic(url: string): boolean {
    const lastKnownPath = this.mainFlowPath.indexOf(this._previousKnownPath);
    const currentPath = this.mainFlowPath.indexOf(url);

    return lastKnownPath > currentPath;
  }
}
